import { APIService } from '../../../lib/api/APIService';

// Apps API class extending the base APIService
class AppsAPI extends APIService {
  async getApps() {
    return this.get('/api/apps/');
  }

  /** Public list for landing page (no auth). Uses same response shape as getApps(). */
  async getAppsPublic() {
    return this.get('/api/apps/public', { skipAuth: true });
  }

  /** Public list filtered by creator (no auth). creatorName required for filtering. */
  async getAppsByCreatorPublic(creatorName) {
    const q = creatorName ? `?creator=${encodeURIComponent(creatorName)}` : '';
    return this.get(`/api/apps/public${q}`, { skipAuth: true });
  }

  /** Public creator profile details by creator slug (no auth). */
  async getCreatorProfilePublic(creatorSlug) {
    return this.get(`/api/apps/public/creator-profile/${encodeURIComponent(creatorSlug)}`, { skipAuth: true });
  }

  /** Public list of all creators (no auth). */
  async getCreatorsPublic() {
    return this.get('/api/apps/public/creators', { skipAuth: true });
  }

  /** Public single app for landing page (no auth). Uses same response shape as getAppById(). */
  async getAppByIdPublic(id) {
    return this.get(`/api/apps/public/${id}`, { skipAuth: true });
  }

  /** Public quizzes for productivity app on landing page (no auth). */
  async getAppQuizzesPublic(appId) {
    return this.get(`/api/apps/public/${appId}/quizzes`, { skipAuth: true });
  }

  /** Apps ordered for current user (for_you first by subject), with for_you_subjects. */
  async getAppsForUser() {
    return this.get('/api/apps/for-user');
  }

  /** Usage stats: contribution heatmap, recent completions, top scores, total_time_seconds. */
  async getUsageStats() {
    return this.get('/api/apps/usage/stats');
  }

  /** Apps the user has purchased/added (Your apps from user_apps table). Same shape as getApps(). */
  async getMyApps() {
    return this.get('/api/apps/my-apps');
  }

  /** What the account owns (access.owned shape). Rejects on 503: never an empty object. */
  async getOwned() {
    return this.get('/api/apps/owned');
  }

  /** Free Beginnings choice (B5). Body {exam?, package?, subject?}; resolves to {granted, owned}. */
  async chooseBeginnings(body) {
    return this.post('/api/purchases/beginnings', body);
  }

  /** Public Oxbridge subject list (no auth). */
  async getOxbridgeSubjects() {
    return this.get('/api/interviews/oxbridge-subjects', { skipAuth: true });
  }

  /** Buy/purchase an app - adds it to user's collection */
  async buyApp(appId) {
    return this.post(`/api/apps/${appId}/buy`);
  }

  /** Remove/unbuy an app from user's collection */
  async removeApp(appId) {
    return this.post(`/api/apps/${appId}/remove`);
  }

  async getAppById(id) {
    return this.get(`/api/apps/${id}`);
  }

  async updateApp(id, data) {
    return this.put(`/api/apps/${id}`, data);
  }

  async getAppsHealth() {
    return this.get('/api/apps/health');
  }

  async getAppQuizzes(appId) {
    return this.get(`/api/apps/${appId}/quizzes`);
  }

  /**
   * Record app usage for tracking (case study, video interview, reasoning test, quiz, experience completed, app opened).
   * @param {string} appId - App UUID
   * @param {string} activityType - 'case_study_completed' | 'video_interview_completed' | 'reasoning_test_completed' | 'quiz_completed' | 'experience_completed' | 'app_opened'
   * @param {{ referenceType?: string, referenceId?: string, metadata?: object }} options - Optional reference and metadata
   */
  async recordUsage(appId, activityType, options = {}) {
    const body = {
      app_id: appId,
      activity_type: activityType,
      ...(options.referenceType && { reference_type: options.referenceType }),
      ...(options.referenceId != null && { reference_id: options.referenceId }),
      ...(options.metadata && typeof options.metadata === 'object' && { metadata: options.metadata }),
    };
    return this.post('/api/apps/usage', body);
  }

  async getExcelAuthUrl(returnTo, templateId) {
    const params = new URLSearchParams();
    if (returnTo) params.set('return_to', returnTo);
    if (templateId) params.set('template_id', templateId);
    const qs = params.toString();
    return this.get(`/api/excel/auth/start${qs ? `?${qs}` : ''}`);
  }

  async getCaiusResearch(subjectLabel, question) {
    return this.post('/api/apps/caius-explore/research', { subjectLabel, question });
  }

  async getCaiusFeedback(subjectLabel, question, details, draftText) {
    return this.post('/api/apps/caius-explore/feedback', { subjectLabel, question, details, draftText });
  }

  async getCaiusMore(key, subjectLabel, question, existing) {
    return this.post('/api/apps/caius-explore/more', { key, subjectLabel, question, existing });
  }
}

// Create and export apps API instance
export const appsAPI = new AppsAPI();
